import { Observable } from 'rxjs';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { UtilityService } from 'app/services/utility.service';
import { BaseApiService } from 'app/services/BaseApiService';
import { FormCommentView } from 'app/models/form/FormCommentView';
import { environment } from 'environments/environment';
import { FileUploadService } from './file-upload-service';

export class BaseFormService extends BaseApiService {
	private fileUploadService: FileUploadService;
	constructor(urlPath: string, httpClient: HttpClient, utilityService: UtilityService) {
		super(urlPath, httpClient, utilityService);
	}
	setFileUploadService(fileUploadService: FileUploadService) {
		this.fileUploadService = fileUploadService;
	}
	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient.post<any>(`${this.getUrl(clientId)}/${id}/decline`, rejectionReason).pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.getUrl(clientId)}/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${this.getUrl(clientId)}/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.getUrl(clientId)}/${id}/review`, {}).pipe();
	}

	sendComment(clientId: string, formId: string, item: FormCommentView, file: File): Observable<FormCommentView> {
		const formData: FormData = new FormData();
		if (file !== null && file !== undefined) {
			formData.append(file.name, file);
		}
		formData.append('model', JSON.stringify(item));
		return this.httpClient.post<FormCommentView>(`${this.getUrl(clientId)}/${formId}/comments`, formData).pipe();
	}

	uploadDocument(clientId: string, formId, formData: FormData): Observable<HttpEvent<any>> {
		const options = {
			headers: new HttpHeaders({
				'x-fileHash': formData.get('fileHash').toString(),
			}),
		};
		const req = new HttpRequest('POST', `${this.getUrl(clientId)}/${formId}/documents`, formData, {
			reportProgress: true,
			responseType: 'json',
			...options,
		});
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.getUrl(clientId)}/${id}`).pipe();
	}

	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.getUrl(clientId)}/${formId}/documents/${docId}`);
	}

	createFormWithDocuments(clientId: string, request: any): Observable<any> {
		return this.httpClient.post<any>(`${this.getUrl(clientId)}`, this.setupRequest(request)).pipe();
	}

	/**
	 * CCB-59. Creates the form already delegated to another user, in one request.
	 *
	 * A separate endpoint from createFormWithDocuments because the server skips the required-field rules on
	 * this one. Doing both halves in a single call also means a form can never end up saved but not
	 * delegated, which a save-then-delegate pair leaves behind whenever the second request fails.
	 */
	createDelegatedDraft(
		clientId: string,
		request: Record<string, unknown>,
		delegateToUserId: string
	): Observable<{ id: string }> {
		return this.httpClient
			.post<{ id: string }>(`${this.getUrl(clientId)}/delegated-draft/${delegateToUserId}`, this.setupRequest(request))
			.pipe();
	}

	updateFormWithDocuments(clientId: string, formId: any, request: any): Observable<any> {
		return this.httpClient.put<any>(`${this.getUrl(clientId)}/${formId}`, this.setupRequest(request)).pipe();
	}

	setupRequest(request: any): FormData {
		const formData: FormData = new FormData();

		for (let i = 0; i < request.attachments.length; i++) {
			const name = `attachment-${i + 1}`;
			formData.append(name, request.attachments[i].file);
			request.attachments[i].name = name;
		}
		formData.append('model', JSON.stringify(request));
		return formData;
	}

	patchForm(clientId: string, formId: string, pathObject: {}): Observable<any> {
		let requestObject = [];
		for (let key in pathObject) {
			requestObject.push({
				op: 'replace',
				path: `/${key}`,
				value: pathObject[key],
			});
		}

		return this.httpClient.patch(`${this.getUrl(clientId)}/${formId}`, requestObject).pipe();
	}
	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.getUrl(clientId)}/${id}/documents`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.getUrl(clientId)}/${id}/approval`, {
				userId,
			})
			.pipe();
	}
	allowBudgetToleranceOverride(clientId: string, formId: string, approverId: string): Observable<any> {
		return this.httpClient.patch(`${this.getUrl(clientId)}/${formId}/approver/${approverId}`, {}).pipe();
	}
	submitBudgetToleranceOverrideRequest(
		clientId: string,
		formId: string,
		request: {
			isBudgetToleranceOverrideAllowed: boolean;
			budgetToleranceApprovers: any[];
			budgetToleranceErrors: any[];
		}
	): Observable<any> {
		return this.httpClient.patch(`${this.getUrl(clientId)}/${formId}/budget-tolerance-override-request`, request);
	}
	uploadAttachment(clientId: string, formId: string, formType: string, file: File): Observable<number> {
		return this.fileUploadService.uploadAttachment(clientId, formId, formType, file);
	}
	uploadAttachments(clientId: string, formId: string, formType: string, files: File[]): Promise<Observable<number>[]> {
		return this.fileUploadService.uploadAttachments(clientId, formId, formType, files);
	}
}
